import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  signal,
  Signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import {
  NgCommonModule,
  TypedFormControl,
  TypedFormGroup,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import { MarketingCampaign } from '@principle-theorem/principle-core';
import {
  ALLOWED_MARKETING_CAMPAIGN_SCOPES,
  ICampaignEntryResetRules,
  IMarketingCampaign,
  MARKETING_CAMPAIGN_STEP_CONVERSION_DESCRIPTIONS,
  MarketingCampaignScope,
  MarketingCampaignStepConversionType,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import { getFrequencyUnit } from '@principle-theorem/reporting';
import {
  CustomRecurrenceFrequency,
  getEnumValues,
  IFrequencyDisplay,
  RECURRENCE_FREQUENCY_DISPLAY_MAP,
  splitCamel,
  titlecase,
  WithRef,
} from '@principle-theorem/shared';
import { uniq } from 'lodash';
import * as moment from 'moment-timezone';
import { combineLatest } from 'rxjs';
import { map, startWith, switchMap, take } from 'rxjs/operators';
import {
  IMarketingCampaignStepSummary,
  MarketingCampaignStepSummary,
} from '../campaign-steps-summary/campaign-step-summary';
import { CampaignStepsSummaryComponent } from '../campaign-steps-summary/campaign-steps-summary.component';

export interface ICampaignEntryFormData {
  scope: MarketingCampaignScope;
  conversionType?: MarketingCampaignStepConversionType;
  entryResetRules: ICampaignEntryResetRules;
}

@Component({
  selector: 'pr-campaign-entry-settings',
  templateUrl: './campaign-entry-settings.component.html',
  styleUrl: './campaign-entry-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatTooltipModule,
    CampaignStepsSummaryComponent,
    InformationBoxComponent,
    MatIconModule,
  ],
})
export class CampaignEntrySettingsComponent {
  campaign = input.required<WithRef<IMarketingCampaign>>();
  scopes = ALLOWED_MARKETING_CAMPAIGN_SCOPES;
  conversionTypes = getEnumValues(MarketingCampaignStepConversionType);
  frequencies: IFrequencyDisplay[] = RECURRENCE_FREQUENCY_DISPLAY_MAP;
  reentryScopes = signal(ALLOWED_MARKETING_CAMPAIGN_SCOPES);
  form = new TypedFormGroup<ICampaignEntryFormData>({
    scope: new TypedFormControl<MarketingCampaignScope>(
      TemplateScope.Patient,
      Validators.required
    ),
    conversionType: new TypedFormControl<MarketingCampaignStepConversionType>(
      undefined
    ),
    entryResetRules: new TypedFormGroup<ICampaignEntryResetRules>({
      multipleEntry: new TypedFormControl<boolean>(false),
      reentryScope: new TypedFormControl<MarketingCampaignScope | undefined>(
        undefined
      ),
      maxEntryCount: new TypedFormControl<number | undefined>(undefined),
      frequencyCount: new TypedFormControl<number>(1, Validators.required),
      frequencyType: new TypedFormControl(
        CustomRecurrenceFrequency.Daily,
        Validators.required
      ),
    }),
  });
  formChange = output<ICampaignEntryFormData>();

  campaignSubject: Signal<string>;
  entryScope: Signal<string>;
  multipleEntriesAllowed: Signal<boolean>;
  showEntryTimingWarning = signal(false);
  stepSummary: Signal<IMarketingCampaignStepSummary>;
  patientSubject = titlecase(splitCamel(TemplateScope.Patient));

  constructor() {
    const campaign$ = toObservable(this.campaign);
    const formValue$ = validFormGroupChanges$(this.form);
    const currentFormValue$ = combineLatest([
      campaign$,
      formValue$.pipe(startWith(undefined)),
    ]).pipe(
      map(
        ([campaign, formValue]): ICampaignEntryFormData =>
          formValue ?? this._toFormData(campaign)
      )
    );

    this.campaignSubject = toSignal(
      currentFormValue$.pipe(
        map((formValue) => {
          const campaignSubject = formValue.scope;
          return titlecase(splitCamel(campaignSubject));
        })
      ),
      { initialValue: '[CAMPAIGN SUBJECT]' }
    );
    this.entryScope = toSignal(
      currentFormValue$.pipe(
        map((formValue) => {
          const entryScope =
            formValue.entryResetRules.reentryScope ?? formValue.scope;
          return titlecase(splitCamel(entryScope));
        })
      ),
      { initialValue: '[ENTRY SCOPE]' }
    );
    this.multipleEntriesAllowed = toSignal(
      currentFormValue$.pipe(
        map((formValue) => formValue.entryResetRules.reentryScope !== undefined)
      ),
      { initialValue: false }
    );
    campaign$.pipe(take(1), takeUntilDestroyed()).subscribe((campaign) => {
      this._setInitialFormValues(campaign);
    });

    const stepSummary$ = campaign$.pipe(
      switchMap((campaign) =>
        MarketingCampaignStepSummary.getStepSummary$(campaign)
      )
    );
    this.stepSummary = toSignal(stepSummary$, {
      initialValue: {
        activeSteps: 0,
        durationInDays: 0,
      },
    });

    combineLatest([stepSummary$, currentFormValue$])
      .pipe(
        map(([stepSummary, formValue]) =>
          this._hasEntryTimingWarning$(stepSummary, formValue)
        ),
        takeUntilDestroyed()
      )
      .subscribe((hasWarning) => this.showEntryTimingWarning.set(hasWarning));

    formValue$
      .pipe(takeUntilDestroyed())
      .subscribe((value) =>
        this.formChange.emit(this._toPersistedValue(value))
      );
  }

  getConversionTypeTooltip(type: MarketingCampaignStepConversionType): string {
    return MARKETING_CAMPAIGN_STEP_CONVERSION_DESCRIPTIONS[type];
  }

  setUnlimitedEntries(): void {
    this.form.controls.entryResetRules.patchValue({ maxEntryCount: undefined });
  }

  campaignSubjectChange(event: MatSelectChange): void {
    const campaignSubject = event.value as MarketingCampaignScope;
    this._setValidReentryScopes(campaignSubject);

    if (this.form.value.entryResetRules.reentryScope) {
      this.form.controls.entryResetRules.patchValue({
        reentryScope: campaignSubject,
      });
    }
  }

  private _setInitialFormValues(campaign: WithRef<IMarketingCampaign>): void {
    this.form.patchValue(this._toFormData(campaign), { emitEvent: false });
    this._setValidReentryScopes(campaign.scope);
  }

  private _toFormData(
    campaign: WithRef<IMarketingCampaign>
  ): ICampaignEntryFormData {
    return {
      scope: campaign.scope,
      conversionType: campaign.conversionType,
      entryResetRules: MarketingCampaign.resolveEntryResetRules(campaign),
    };
  }

  // Both consuming dialogs save this output unchanged.
  private _toPersistedValue(
    formValue: ICampaignEntryFormData
  ): ICampaignEntryFormData {
    return {
      ...formValue,
      entryResetRules: MarketingCampaign.normaliseEntryResetRules(
        formValue.entryResetRules
      ),
    };
  }

  private _hasEntryTimingWarning$(
    stepSummary: IMarketingCampaignStepSummary,
    formValue: ICampaignEntryFormData
  ): boolean {
    const unit = getFrequencyUnit(formValue.entryResetRules.frequencyType);
    const resetDurationInDays = moment
      .duration(formValue.entryResetRules.frequencyCount, unit)
      .asDays();
    const hasPotentialEntryOverlap =
      stepSummary.durationInDays > resetDurationInDays;
    return hasPotentialEntryOverlap;
  }

  private _setValidReentryScopes(
    campaignSubject: MarketingCampaignScope
  ): void {
    const reentryScopes: MarketingCampaignScope[] = uniq([
      TemplateScope.Patient,
      campaignSubject,
    ]);
    this.reentryScopes.set(reentryScopes);
  }
}
